import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { Lock, Unlock, Shield, ShieldOff, Loader2 } from 'lucide-react';
import { DashboardLayout } from '@/components/layouts/DashboardLayout';
import { useAuth } from '@/contexts/AuthContext';
import { useApiClient } from '@/lib/api';
import { getUserErrorMessage } from '@/lib/error-messages';

interface User {
    id: string;
    email: string;
    first_name: string | null;
    last_name: string | null;
    is_locked: boolean;
    is_admin: boolean;
}

const AdminUsers: React.FC = () => {
    const { user: currentUser } = useAuth();
    const queryClient = useQueryClient();
    const api = useApiClient().api;
    const [confirmDialog, setConfirmDialog] = useState<{
        isOpen: boolean;
        title: string;
        description: string;
        action: () => void;
    }>({
        isOpen: false,
        title: '',
        description: '',
        action: () => { },
    });

    // Fetch all users
    const { data: users, isLoading, error } = useQuery({
        queryKey: ['admin-users'],
        queryFn: async () => {
            try {
                return await api.getUsers();
            } catch (error) {
                throw new Error('Failed to fetch users');
            }
        }
    });

    // Update user mutation
    const updateUserMutation = useMutation({
        mutationFn: async ({ userId, data }: { userId: string; data: { is_locked?: boolean; is_admin?: boolean } }) => {
            let response;

            if (data.is_admin !== undefined) {
                response = await api.setAdminStatus(userId, data.is_admin);
            } else if (data.is_locked !== undefined) {
                response = data.is_locked
                    ? await api.lockUser(userId)
                    : await api.unlockUser(userId);
            } else {
                throw new Error('Invalid update: must specify is_locked or is_admin');
            }

            return response;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin-users'] });
            toast.success('User updated successfully');
        },
        onError: (error: Error) => {
            toast.error(
                getUserErrorMessage(
                    error,
                    'Unable to update this user right now. Please try again later.'
                )
            );
        },
    });

    const handleToggleLock = (user: User) => {
        if (user.is_admin) {
            toast.error("Cannot lock admin users");
            return;
        }

        setConfirmDialog({
            isOpen: true,
            title: user.is_locked ? 'Unlock User' : 'Lock User',
            description: user.is_locked
                ? `Are you sure you want to unlock ${user.email}?`
                : `Are you sure you want to lock ${user.email}? They will not be able to log in.`,
            action: () => {
                updateUserMutation.mutate({
                    userId: user.id,
                    data: { is_locked: !user.is_locked },
                });
            },
        });
    };

    const handleToggleAdmin = (user: User) => {
        if (user.id === currentUser?.id && user.is_admin) {
            toast.error("You cannot remove your own admin status");
            return;
        }

        if (user.is_locked) {
            toast.error("Cannot make locked users admins");
            return;
        }

        setConfirmDialog({
            isOpen: true,
            title: user.is_admin ? 'Remove Admin' : 'Make Admin',
            description: user.is_admin
                ? `Are you sure you want to remove admin privileges from ${user.email}?`
                : `Are you sure you want to make ${user.email} an admin? They will have full access to all features.`,
            action: () => {
                updateUserMutation.mutate({
                    userId: user.id,
                    data: { is_admin: !user.is_admin },
                });
            },
        });
    };

    if (isLoading) {
        return (
            <DashboardLayout>
                <div className="flex items-center justify-center h-64">
                    <Loader2 className="h-8 w-8 animate-spin text-interview-blue" />
                </div>
            </DashboardLayout>
        );
    }

    if (error) {
        return (
            <DashboardLayout>
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                    {getUserErrorMessage(
                        error,
                        'Unable to load users right now. Please try again later.'
                    )}
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <div className="space-y-6">
                <div>
                    <h1 className="text-2xl font-bold">User Management</h1>
                    <p className="text-muted-foreground">Manage user accounts and permissions</p>
                </div>

                <div className="border rounded-lg overflow-hidden">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Email</TableHead>
                                <TableHead>Name</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>Admin</TableHead>
                                <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {users && users.length > 0 ? (
                                users.map((user: User) => (
                                    <TableRow key={user.id}>
                                        <TableCell>{user.email}</TableCell>
                                        <TableCell>
                                            {user.first_name && user.last_name
                                                ? `${user.first_name} ${user.last_name}`
                                                : 'Not provided'}
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-2">
                                                {user.is_locked ? (
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                                        Locked
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                        Active
                                                    </span>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-2">
                                                {user.is_admin ? (
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                                        Admin
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                                        User
                                                    </span>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex justify-end gap-2">
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => handleToggleLock(user)}
                                                    disabled={updateUserMutation.isPending || (user.is_admin && !user.is_locked)}
                                                >
                                                    {user.is_locked ? (
                                                        <>
                                                            <Unlock className="h-4 w-4 mr-1" />
                                                            Unlock
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Lock className="h-4 w-4 mr-1" />
                                                            Lock
                                                        </>
                                                    )}
                                                </Button>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => handleToggleAdmin(user)}
                                                    disabled={updateUserMutation.isPending || (user.id === currentUser?.id && user.is_admin) || user.is_locked}
                                                >
                                                    {user.is_admin ? (
                                                        <>
                                                            <ShieldOff className="h-4 w-4 mr-1" />
                                                            Remove Admin
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Shield className="h-4 w-4 mr-1" />
                                                            Make Admin
                                                        </>
                                                    )}
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                                        No users found
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </div>
            </div>

            <AlertDialog open={confirmDialog.isOpen} onOpenChange={(open) => setConfirmDialog(prev => ({ ...prev, isOpen: open }))}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{confirmDialog.title}</AlertDialogTitle>
                        <AlertDialogDescription>{confirmDialog.description}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => {
                            confirmDialog.action();
                            setConfirmDialog(prev => ({ ...prev, isOpen: false }));
                        }}>
                            Confirm
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </DashboardLayout>
    );
};

export default AdminUsers; 
